import { Button } from "components/ui/Button";
import {
  useApplyStageSuggestion,
  useStageSuggestion,
} from "hooks/use-pipeline-email-api";
import { type FC, useState } from "react";
import { toast } from "react-toastify";
import { getStageLabel } from "routes/crm/utils";
import { toastError } from "utils/toast-utils";

export type StageSuggestionPanelProps = {
  pipelineId: string;
  /** 候補適用後に呼ぶ（Sales Board 側で refetch 等） */
  onApplied?: () => void;
};

/**
 * Sales Board カード上のステージ遷移候補 UI。
 * 返信受領時に「提案準備 → 提案済」などの候補を提示し、営業が判断する。
 * @param {StageSuggestionPanelProps} props props
 * @return {JSX.Element|null} panel
 */
export const StageSuggestionPanel: FC<StageSuggestionPanelProps> = ({
  pipelineId,
  onApplied,
}) => {
  const { suggestion, refetch } = useStageSuggestion({ pipelineId });
  const { applyStageSuggestion, loading } = useApplyStageSuggestion();
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) return null;
  if (!suggestion?.suggestedStage) return null;

  /**
   * 適用
   * @return {Promise<void>}
   */
  const handleApply = async () => {
    if (!suggestion.suggestedStage) return;
    try {
      await applyStageSuggestion(pipelineId, suggestion.suggestedStage);
      toast.success("ステージを更新しました");
      await refetch();
      onApplied?.();
    } catch {
      toastError("ステージの更新に失敗しました");
    }
  };

  return (
    <div className="inline-flex items-center gap-2 rounded-md bg-violet-50 border border-violet-200 px-2 py-1 text-xs">
      <span>💡</span>
      <span>
        <span className="text-slate-600">候補:</span>{" "}
        <span className="font-medium">
          {getStageLabel(suggestion.suggestedStage)}
        </span>{" "}
        に進める?
      </span>
      <Button
        type="button"
        size="xs"
        variant="default"
        onClick={handleApply}
        disabled={loading}
      >
        はい
      </Button>
      <Button
        type="button"
        size="xs"
        variant="ghost"
        onClick={() => setDismissed(true)}
      >
        いいえ
      </Button>
    </div>
  );
};
